import { NgClass, NgTemplateOutlet, SlicePipe } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  type ElementRef,
  inject,
  input,
  model,
  type OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import type {
  InsightData,
  InsightSuggestion,
  NavigateData,
} from '@bizzdesign/ai-assistant/workflows';
import {
  createFrameDataSchema,
  insightDataSchema,
  navigateDataSchema,
  optionsDataSchema,
  THINKING_NOTE_ERROR,
} from '@bizzdesign/ai-assistant/workflows';
import {
  getNavigationPlace,
  type ChatSessionRename,
  type ChatSessionScope,
  type ChatSessionSort,
  type ChatSessionSummary,
  isContinuationMessage,
  navigationPlaceForDestination,
  parseDeliverableReferenceUrl,
  type AssistantPlan,
  type MessageNavigationPlace,
  type MessageNavigationPageName,
} from '@bizzdesign/ai-assistant/data-access';
import { AiAssistantPlanComponent } from '../ai-assistant-plan/ai-assistant-plan.component';
import { detectMcpConnectPart } from '@bizzdesign/ai-assistant/mcp-client-auth';
import {
  assertIsUUID,
  generateUUID,
  type UUID,
} from '@bizzdesign/bizzdesign-sdk/uuid';
import { type HighchartsData } from '@bizzdesign/data-access-highcharts';
import { HighchartsContainerComponent } from '@bizzdesign/feature-highcharts';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { TranslocoService } from '@jsverse/transloco';
import type {
  AttachmentMetric,
  HighchartsContent,
  MappingSpec,
} from '@bizzdesign/shared/models';
import { RenderMarkdownPipe } from '@bizzdesign/shared/pipes';
import { visualizationTemplateRegister } from '@bizzdesign/shared/visualization-templates';
import {
  aiAssistantAccordionPreset,
  chatTextAreaPreset,
} from '@bizzdesign/styles';
import { BadgeComponent } from '@bizzdesign/ui-badge';
import {
  DocumentUploadChipComponent,
  ThumbnailComponent,
} from '@bizzdesign/ui-components';
import { patchState, signalState } from '@ngrx/signals';
import {
  type ChatStatus,
  type FileUIPart,
  isDataUIPart,
  type UIMessage,
} from 'ai';
import { QuillModule } from 'ngx-quill';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { type Observable, of } from 'rxjs';
import {
  ACCEPTED_ATTACHMENT_ACCEPT,
  type AiAssistantFeedbackV2Config,
  type AttachmentRejectionReason,
  baseRoutes,
  fileAttachmentMetric,
  fileSizeLabel,
  fileTypeIcon,
  fileTypeLabel,
  isImageAttachment,
  maxAttachmentSizeBytes,
  validateAttachment,
} from '@bizzdesign/shared/utils';
import { fileToFileUIPart } from '../../utils/file-attachment.util';
import { AiAccordionHeaderComponent } from '../ai-accordion-header/ai-accordion-header.component';
import { AiChatHistoryComponent } from '../ai-chat-history/ai-chat-history.component';
import { AiAssistantSuggestionsV2Component } from '../ai-assistant-suggestions-v2/ai-assistant-suggestions-v2.component';
import { AiToolbarComponent } from '../ai-toolbar/ai-toolbar.component';
import {
  AssistantFeedbackFormComponent,
  type FeedbackFormSubmission,
} from '../assistant-feedback-form/assistant-feedback-form.component';
import { nextSoftPromptDecision } from './soft-prompt-schedule';

type AccordionValue =
  | string
  | number
  | Array<string | number>
  | null
  | undefined;

const THINKING_NOTE_I18N_KEY = 'featureAiAssistant.chat.thinkingDefault';
const NAVIGATION_LOCATION_ICON = 'ui-location';

/**
 * Feedback metadata emitted for one assistant response.
 *
 * @property message - The assistant message being rated.
 * @property comment - Optional free-text feedback from the user.
 * @property categories - Optional feedback categories selected in the form.
 * @property rating - Optional rating value submitted with the feedback.
 * @property prompted - Whether the feedback came from an explicit prompt or a voluntary action.
 */
export interface FeedbackEmission {
  readonly message: UIMessage;
  readonly comment?: string;
  readonly categories?: ReadonlyArray<string>;
  readonly rating?: 1 | 2 | 3 | 4 | 5;
  /**
   * Distinguishes voluntary submissions (toolbar thumb click) from
   * soft-prompt-driven ones (modal). Tracked per emission rather than
   * derived from the feature-flag mode, since a user can still submit
   * feedback voluntarily even when the soft-prompt flag is on.
   */
  readonly prompted: 'voluntary' | 'soft';
}

/**
 * Payload emitted when the user sends a chat message, carrying the typed
 * text and any file attachments selected in the composer.
 */
export type MessageSendPayload = {
  readonly text: string;
  readonly files: FileUIPart[];
};

/**
 * A clickable option the user picked. `selectionId` is a fresh id generated
 * per click — unlike `messageId`, it is never reused by a retry from the
 * same message — so its result can be correlated back to this exact click.
 */
export type OptionSelection = {
  readonly selectionId: string;
  readonly messageId: string;
  readonly label: string;
};

/**
 * Outcome of sending a picked option's label, tagged with the `selectionId`
 * of the click it belongs to so a stale result (e.g. a slow prior attempt)
 * cannot be applied to a later, unrelated selection — including a retry from
 * the same message, which would otherwise share the same `messageId`.
 */
export type OptionSendResult = {
  readonly selectionId: string;
  readonly messageId: string;
  readonly success: boolean;
};

/**
 * A picked file that was rejected before it could be attached, carrying its
 * analytics metric and the reason it was rejected. Emitted for upload-failure
 * tracking.
 */
export type RejectedAttachment = AttachmentMetric & {
  readonly reason: AttachmentRejectionReason;
};

/**
 * A rejected attachment, used to render an inline validation message.
 *
 * @property reason - `'size'` when the file exceeds the size cap, `'type'`
 * when its format is not supported.
 * @property filename - Name of the rejected file, shown in the message.
 * @property maxMb - For a `'size'` rejection, the cap that was exceeded, in MB.
 * @property precedingAttachmentCount - Number of pending attachments that
 * existed when this rejection occurred, so it can render at its actual
 * chronological position instead of always trailing the attachment row.
 */
type AttachmentError = {
  readonly reason: AttachmentRejectionReason;
  readonly filename: string;
  readonly maxMb?: number;
  readonly precedingAttachmentCount: number;
};

/**
 * One entry in the pending-attachment row: either a successfully attached
 * file or the current validation error, ordered by when each occurred.
 */
type PendingAttachmentRowItem =
  | { readonly kind: 'attachment'; readonly attachment: FileUIPart }
  | { readonly kind: 'error'; readonly error: AttachmentError };

/**
 * Rendering data for a single attachment on a chat message: the underlying
 * file part, whether it should render as an image thumbnail, and its
 * resolved preview stream.
 */
type AttachmentViewModel = {
  readonly part: FileUIPart;
  readonly isImage: boolean;
  readonly preview$: Observable<string | null> | undefined;
};

const DEFAULT_SOFT_PROMPT_INTERVAL = 3;
const DEFAULT_SOFT_PROMPT_MAX_INTERVAL = 30;

type NavigationLink = {
  readonly data: NavigateData;
  readonly place: MessageNavigationPlace;
};

function navigationLinkForPart(
  part: UIMessage['parts'][number],
): NavigationLink | undefined {
  if (!isDataUIPart(part)) return undefined;
  if (part.type === 'data-navigate-data') {
    const parsed = navigateDataSchema.safeParse(part.data);
    return parsed.success
      ? {
          data: parsed.data,
          place: navigationPlaceForDestination(parsed.data),
        }
      : undefined;
  }
  if (part.type === 'data-create-frame-data') {
    const parsed = createFrameDataSchema.safeParse(part.data);
    if (!parsed.success) return undefined;
    const data: NavigateData = {
      kind: 'frame',
      frameId: parsed.data.frameId,
      label: parsed.data.name ?? `New frame in ${parsed.data.workspaceName}`,
    };
    return { data, place: navigationPlaceForDestination(data) };
  }
  return undefined;
}

function navigationLinksForMessage(message: UIMessage): NavigationLink[] {
  if (message.role !== 'assistant') return [];
  return message.parts.flatMap((part) => {
    const link = navigationLinkForPart(part);
    return link === undefined ? [] : [link];
  });
}

function navigationLinkEntriesForMessage(
  message: UIMessage,
): [string, NavigationLink[]][] {
  const links = navigationLinksForMessage(message);
  return links.length > 0 ? [[message.id, links]] : [];
}

function navigationPlaceEntriesForMessage(
  message: UIMessage,
): [string, MessageNavigationPlace][] {
  const place = getNavigationPlace(message);
  return place === undefined ? [] : [[message.id, place]];
}

function navigationLinkLabel(link: NavigationLink): string {
  return link.place.label;
}

function navigationLinkIcon(_link: NavigationLink): string {
  // Keep the parameter for a future icon split by navigation kind.
  return NAVIGATION_LOCATION_ICON;
}

function navigationPlaceIcon(_place: MessageNavigationPlace): string {
  // Keep the parameter for a future icon split by destination kind.
  return NAVIGATION_LOCATION_ICON;
}

function navigationPlaceCommands(
  place: MessageNavigationPlace,
): string | string[] {
  switch (place.kind) {
    case 'page':
      return place.page === 'frame'
        ? [baseRoutes.frames, place.frameId]
        : navigationPageCommands(place.page);
    case 'view':
      return [baseRoutes.views, place.viewId];
    case 'workspace':
      return [baseRoutes.workspaces, place.workspaceId];
  }
}

function navigationPageCommands(page: MessageNavigationPageName): string[] {
  switch (page) {
    case 'home':
      return [baseRoutes.home];
    case 'search':
      return [baseRoutes.home, 'search'];
    case 'workspaces':
      return [baseRoutes.workspaces];
    case 'archive':
      return [baseRoutes.archive];
    case 'data-management':
      return [baseRoutes.dataManagement];
  }
}

@Component({
  selector: 'bd-ai-assistant-v2',
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    SlicePipe,
    RouterLink,
    FormsModule,
    AccordionModule,
    ButtonModule,
    DividerModule,
    TextareaModule,
    TooltipModule,
    InputTextModule,
    QuillModule,
    SharedI18nModule,
    RenderMarkdownPipe,
    BadgeComponent,
    DocumentUploadChipComponent,
    ThumbnailComponent,
    AiAssistantSuggestionsV2Component,
    AiAccordionHeaderComponent,
    AiChatHistoryComponent,
    AiToolbarComponent,
    AssistantFeedbackFormComponent,
    HighchartsContainerComponent,
    AiAssistantPlanComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ai-assistant-v2.component.html',
  styleUrl: './ai-assistant-v2.component.scss',
})
/**
 * Presentational chat panel for rendering messages, suggestions, and chat history controls.
 */
export class AiAssistantV2Component implements OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private readonly activeLang = toSignal(this.transloco.langChanges$);

  messages = input.required<UIMessage[]>();
  status = input.required<ChatStatus>();
  suggestions = input<string[]>([]);
  visualSuggestions = input<InsightSuggestion[]>([]);
  refreshingVisualSuggestions = input(false);
  showSuggestionsPanel = input(false);
  insightData = input<InsightData | null>(null);
  feedbackConfig = input<AiAssistantFeedbackV2Config | null>(null);
  fileUploadEnabled = input(false);
  isUploadingAttachments = input(false);
  localAttachmentPreviews = input<Record<string, string>>({});
  thinkingNote = input<string | null>(null);
  startNewChatEnabled = input(false);
  plan = input<AssistantPlan | null>(null);
  continuationPending = input(false);
  awaitingInstructions = input(false);
  chatHistoryEnabled = input(false);
  assistantPanelVisible = input(true);
  chatHistoryPanelOpen = model(false);
  chatSessions = input<ChatSessionSummary[]>([]);
  chatSessionsStatus = input<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  chatSessionsHasMore = input(false);
  chatSessionsSearchTerm = input('');
  chatSessionsSort = input<ChatSessionSort>('lastUsed');
  chatSessionsScope = input<ChatSessionScope>('all');
  hasFrameContext = input(false);
  activeChatSessionId = input<UUID | null>(null);
  chatSessionLoadingId = input<UUID | null>(null);
  chatSessionDeletingIds = input<UUID[]>([]);
  chatSessionRenamingId = input<UUID | null>(null);
  optionSendResult = input<OptionSendResult | null>(null);

  thumbsUp = output<FeedbackEmission>();
  thumbsDown = output<FeedbackEmission>();
  feedbackPrompted = output<{ messageId: string }>();
  feedbackSkipped = output<{ messageId: string }>();
  suggestionClicked = output<string>();
  messageSent = output<MessageSendPayload>();
  navigationRequested = output<NavigateData>();
  attachmentsRejected = output<RejectedAttachment[]>();
  stopRequested = output<void>();
  clearRequested = output<void>();
  chatHistoryOpened = output<void>();
  chatSessionSelected = output<UUID>();
  chatSessionsDeleteRequested = output<UUID[]>();
  chatSessionsRenameRequested = output<ChatSessionRename>();
  chatSessionsLoadMoreRequested = output<void>();
  chatSessionsSearchRequested = output<string>();
  chatSessionsSortChanged = output<ChatSessionSort>();
  /**
   * Emits the chat session scope selected by the user.
   *
   * @remarks
   * Emits `contextual` to limit results to the active frame context or `all`
   * to include every accessible chat session.
   */
  chatSessionsScopeChanged = output<ChatSessionScope>();
  downloadAttachment = output<FileUIPart>();
  requestAttachmentPreview = output<FileUIPart>();
  selectedVisualSuggestion = output<InsightSuggestion>();
  insightChartClicked = output<InsightData['insights'][number]>();
  optionSelected = output<OptionSelection>();
  connectMcpClicked = output<{
    mcpId: string;
    mcpName: string;
    endpointUrl: string;
  }>();

  protected readonly isProcessing = computed(
    () => this.status() === 'submitted' || this.status() === 'streaming',
  );

  protected readonly visibleThinkingNote = computed(() => {
    this.activeLang();
    if (this.status() === 'ready') {
      return null;
    }
    const note = this.thinkingNote();
    if (note === THINKING_NOTE_ERROR) {
      return this.transloco.translate(THINKING_NOTE_ERROR);
    }
    if (note) {
      return note;
    }
    return this.isProcessing()
      ? this.transloco.translate(THINKING_NOTE_I18N_KEY)
      : null;
  });

  protected readonly isChatAnswered = computed(() => {
    if (this.status() !== 'ready') return false;
    return this.messages().some(
      (m) =>
        m.role === 'assistant' &&
        m.parts.some((p) => p.type === 'text' && p.text.length > 0),
    );
  });

  protected readonly successfulInsights = computed(
    () =>
      this.insightData()
        ?.insights.filter(
          (i) =>
            i.success &&
            visualizationTemplateRegister[i.visualizationId] !== undefined,
        )
        .map((i) => this.toInsightViewModel(i)) ?? [],
  );

  /**
   * Per-message list of `mcp__<slug>__connect` placeholder tool outputs.
   * Each entry renders as a "Connect to {mcpName}" button below the
   * agent's message. The click handler fires `connectMcpClicked`, which
   * the container wires to the store's `connectMcp` method — that's
   * what actually opens the OAuth popup. Triggering it from a real user
   * gesture (the button click) is what flips the browser out of
   * "open as tab" mode and into a true OS popup window.
   */
  protected readonly connectPromptsByMessageId = computed(() => {
    const result: Record<
      string,
      Array<{
        toolCallId: string;
        mcpId: string;
        mcpName: string;
        endpointUrl: string;
      }>
    > = {};
    for (const message of this.messages()) {
      if (message.role !== 'assistant') continue;
      const prompts: Array<{
        toolCallId: string;
        mcpId: string;
        mcpName: string;
        endpointUrl: string;
      }> = [];
      for (const part of message.parts) {
        const detected = detectMcpConnectPart(part);
        if (!detected) continue;
        prompts.push({
          toolCallId: detected.toolCallId,
          mcpId: detected.output.mcpId,
          mcpName: detected.output.mcpName,
          endpointUrl: detected.output.endpointUrl,
        });
      }
      if (prompts.length > 0) {
        result[message.id] = prompts;
      }
    }
    return result;
  });

  protected readonly insightsByMessageId = computed(() => {
    const result: Record<
      string,
      Array<{
        name: string;
        insight: InsightData['insights'][number];
        highchartsData: HighchartsData;
      }>
    > = {};

    for (const message of this.messages()) {
      if (message.role !== 'assistant') continue;

      for (const part of [...message.parts].reverse()) {
        if (!isDataUIPart(part)) continue;
        const parsed = insightDataSchema.safeParse(part.data);
        if (!parsed.success) continue;

        const insights = parsed.data.insights
          .filter(
            (i) =>
              i.success &&
              visualizationTemplateRegister[i.visualizationId] !== undefined,
          )
          .map((i) => this.toInsightViewModel(i));

        if (insights.length > 0) {
          result[message.id] = insights;
        }
        break;
      }
    }

    return result;
  });

  /**
   * Per-message set of clickable choices presented via the `present_options`
   * tool. Distinct from `suggestions()`: those are capped at two, generic
   * follow-up prompts shown once above the chat input; these are an
   * arbitrary number of choices the assistant asked the user to pick from,
   * rendered inline below the specific message that asked.
   */
  protected readonly optionsByMessageId = computed(() => {
    const dismissed = this.dismissedOptionMessageIds();
    const result: Record<string, string[]> = {};

    for (const message of this.messages()) {
      if (message.role !== 'assistant') continue;
      if (dismissed.has(message.id)) continue;

      for (const part of [...message.parts].reverse()) {
        if (!isDataUIPart(part)) continue;
        const parsed = optionsDataSchema.safeParse(part.data);
        if (!parsed.success) continue;

        result[message.id] = parsed.data.options;
        break;
      }
    }

    return result;
  });

  protected readonly navigationLinksByMessageId = computed(() => {
    return Object.fromEntries(
      this.messages().flatMap(navigationLinkEntriesForMessage),
    );
  });
  protected readonly navigationLinkLabel = navigationLinkLabel;
  protected readonly navigationLinkIcon = navigationLinkIcon;
  protected readonly navigationPlaceIcon = navigationPlaceIcon;
  protected readonly navigationPlaceCommands = navigationPlaceCommands;

  protected readonly navigationPlacesByMessageId = computed(() => {
    return Object.fromEntries(
      this.messages().flatMap(navigationPlaceEntriesForMessage),
    );
  });

  protected readonly completedAssistantMessageMap = computed(() => {
    const completed: Record<string, boolean> = {};
    for (const message of this.completedAssistantMessages()) {
      completed[message.id] = true;
    }
    return completed;
  });

  // A chain is mid-flight whenever a follow-up turn is queued or a plan is still
  // being worked through; `status()` alone reads as idle in the gap between turns.
  protected readonly isWorkingThroughChain = computed(() => {
    const activePlan = this.plan();
    return (
      this.continuationPending() ||
      (activePlan !== null && activePlan.failedStep === null)
    );
  });

  protected readonly visibleMessages = computed(() =>
    this.messages().filter(
      (message) =>
        message.role === 'user' ||
        message.parts.some(
          (part) =>
            (part.type === 'text' && part.text.length > 0) ||
            part.type === 'reasoning' ||
            isRenderableDataPart(part),
        ),
    ),
  );

  // Groups messages into turns (prompt + reply) so CSS can reserve the trailing
  // space needed to scroll the newest prompt to the top.
  protected readonly turns = computed(() => {
    const turns: Array<{
      id: string;
      messages: UIMessage[];
      continued: boolean;
    }> = [];
    for (const message of this.visibleMessages()) {
      const currentTurn = turns.at(-1);
      if (message.role === 'user' || !currentTurn) {
        const continued = isContinuationMessage(message);
        turns.push({
          id: message.id,
          messages: continued ? [] : [message],
          continued,
        });
      } else {
        currentTurn.messages.push(message);
      }
    }
    return turns;
  });

  protected readonly messagesWithTextContent = computed(() => {
    const ids = new Set<string>();
    for (const message of this.messages()) {
      if (
        message.parts.some(
          (part) =>
            (part.type === 'text' && part.text.length > 0) ||
            part.type === 'reasoning',
        )
      ) {
        ids.add(message.id);
      }
    }
    return ids;
  });

  protected readonly attachmentsByMessageId = computed(() => {
    const result: Record<string, AttachmentViewModel[]> = {};
    for (const message of this.messages()) {
      const attachments = this.fileParts(message).map(
        (part): AttachmentViewModel => ({
          part,
          isImage: isImageAttachment(part.mediaType),
          preview$: this.filePreview$(part),
        }),
      );
      if (attachments.length > 0) {
        result[message.id] = attachments;
      }
    }
    return result;
  });

  /**
   * Sent images whose deliverable reference has no locally cached preview
   * (e.g. after a page reload, when the session-only cache is empty) and
   * therefore need their preview resolved from the deliverable's content.
   */
  private readonly unresolvedImagePreviews = computed<
    { part: FileUIPart; deliverableId: string }[]
  >(() => {
    const previews = this.localAttachmentPreviews();
    return Object.values(this.attachmentsByMessageId())
      .flat()
      .filter((attachment) => attachment.isImage)
      .map((attachment) => ({
        part: attachment.part,
        deliverableId: parseDeliverableReferenceUrl(attachment.part.url),
      }))
      .filter(
        (
          entry,
        ): entry is {
          part: FileUIPart;
          deliverableId: string;
        } =>
          entry.deliverableId !== undefined &&
          previews[entry.deliverableId] === undefined,
      );
  });

  protected readonly completedAssistantMessages = computed(() => {
    const result: UIMessage[] = [];
    const messages = this.messages();
    const totalCount = messages.length;
    const isStreamingLast = this.isProcessing();
    for (let i = 0; i < totalCount; i += 1) {
      const message = messages[i];
      if (message.role !== 'assistant') continue;
      const isLast = i === totalCount - 1;
      if (isLast && isStreamingLast) continue;
      result.push(message);
    }
    return result;
  });

  feedbackState = signal<Record<string, 'up' | 'down'>>({});
  feedbackSubmitted = signal<Record<string, boolean>>({});
  messageIdShowingFeedback = signal<string | null>(null);
  messageIdThankYouShown = signal<string | null>(null);
  showSoftPrompt = signal<boolean>(false);
  softPromptMessageId = signal<string | null>(null);
  private softPromptsShown = 0;
  private nextSoftPromptAt: number | null = null;
  private previousCompletedCount = 0;
  inputVal = signal('');
  pendingAttachments = signal<FileUIPart[]>([]);
  attachmentError = signal<AttachmentError | null>(null);
  protected readonly isConvertingAttachments = signal(false);
  chatTextAreaPreset = chatTextAreaPreset;
  aiAssistantAccordionPreset = aiAssistantAccordionPreset;
  feedbackComment = model<string>('');

  protected readonly acceptedFileTypes = ACCEPTED_ATTACHMENT_ACCEPT;
  protected readonly fileTypeIcon = fileTypeIcon;
  protected readonly fileTypeLabel = fileTypeLabel;
  protected readonly fileSizeLabel = fileSizeLabel;
  protected readonly hasContent = computed(
    () => this.inputVal().trim() !== '' || this.pendingAttachments().length > 0,
  );

  /**
   * Pending attachments and the current validation error (if any), merged
   * into one chronologically ordered row: the error renders at the position
   * it actually occurred at, not always after every attachment.
   */
  protected readonly pendingAttachmentRow = computed<
    PendingAttachmentRowItem[]
  >(() => {
    const attachments = this.pendingAttachments();
    const error = this.attachmentError();
    const toItem = (attachment: FileUIPart): PendingAttachmentRowItem => ({
      kind: 'attachment',
      attachment,
    });
    if (error === null) {
      return attachments.map(toItem);
    }
    return [
      ...attachments.slice(0, error.precedingAttachmentCount).map(toItem),
      { kind: 'error', error },
      ...attachments.slice(error.precedingAttachmentCount).map(toItem),
    ];
  });

  openPanels = signal<string[]>(['chat']);
  private readonly optimisticChatSessionDeletingIds = signal<UUID[]>([]);
  protected readonly pendingChatSessionDeletingIds = computed(() =>
    Array.from(
      new Set([
        ...this.chatSessionDeletingIds(),
        ...this.optimisticChatSessionDeletingIds(),
      ]),
    ),
  );
  protected readonly chatHistoryBusy = computed(
    () => this.isProcessing() || this.chatSessionLoadingId() !== null,
  );
  protected readonly showChatHistoryPanel = computed(
    () => this.chatHistoryEnabled() && this.chatHistoryPanelOpen(),
  );

  private readonly currentTurnId = computed(() => {
    const turns = this.turns();
    for (let index = turns.length - 1; index >= 0; index -= 1) {
      if (!turns[index].continued) {
        return turns[index].id;
      }
    }
    return null;
  });
  private turnIdScrolledToTop: string | null = null;
  private scrollCurrentTurnFrame: number | undefined;

  private readonly scrollContainer =
    viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private readonly chatInput =
    viewChild<ElementRef<HTMLTextAreaElement>>('chatInput');
  private readonly fileInput =
    viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly attachmentListContainer = viewChild<ElementRef<HTMLElement>>(
    'attachmentListContainer',
  );
  private readonly suggestionDispatchCooldownMs = 1500;
  private readonly suggestionDispatchState = signalState({
    isLocked: false,
  });
  private unlockSuggestionDispatchTimer?: ReturnType<typeof setTimeout>;
  private readonly optionDispatchCooldownMs = 1500;
  private readonly optionDispatchState = signalState({
    isLocked: false,
  });
  private unlockOptionDispatchTimer?: ReturnType<typeof setTimeout>;
  private readonly dismissedOptionMessageIds = signal<ReadonlySet<string>>(
    new Set(),
  );
  protected readonly pendingOptionSelection = signal<OptionSelection | null>(
    null,
  );
  private readonly requestedAttachmentPreviewIds = new Set<string>();

  constructor() {
    let previousChatSessions = this.chatSessions();
    effect(() => {
      const pendingPreviews = this.unresolvedImagePreviews();
      untracked(() => {
        for (const { part, deliverableId } of pendingPreviews) {
          if (!this.requestedAttachmentPreviewIds.has(deliverableId)) {
            this.requestedAttachmentPreviewIds.add(deliverableId);
            this.requestAttachmentPreview.emit(part);
          }
        }
      });
    });

    effect(() => {
      const suggestions = this.visualSuggestions();
      if (suggestions.length > 0) {
        untracked(() => {
          if (!this.openPanels().includes('suggestions')) {
            this.openPanels.update((panels) => [...panels, 'suggestions']);
          }
        });
      }
    });
    effect(() => {
      if (this.assistantPanelVisible()) {
        return;
      }
      untracked(() => {
        this.chatHistoryPanelOpen.set(false);
      });
    });
    effect(() => {
      const config = this.feedbackConfig();
      if (config === null) {
        untracked(() => {
          this.softPromptsShown = 0;
          this.nextSoftPromptAt = null;
          this.previousCompletedCount = 0;
          if (this.showSoftPrompt()) {
            this.showSoftPrompt.set(false);
            this.softPromptMessageId.set(null);
          }
        });
        return;
      }
      const completed = this.completedAssistantMessages();
      const interval = Math.max(
        1,
        config.intervalMessages ?? DEFAULT_SOFT_PROMPT_INTERVAL,
      );
      const maxInterval = Math.max(
        interval,
        config.maxIntervalMessages ?? DEFAULT_SOFT_PROMPT_MAX_INTERVAL,
      );
      untracked(() => {
        const lastCompleted = completed.at(-1);
        const decision = nextSoftPromptDecision(
          {
            softPromptsShown: this.softPromptsShown,
            nextSoftPromptAt: this.nextSoftPromptAt,
            previousCompletedCount: this.previousCompletedCount,
          },
          {
            interval,
            maxInterval,
            completedCount: completed.length,
            isLastCompletedSubmitted:
              lastCompleted !== undefined &&
              this.feedbackSubmitted()[lastCompleted.id] === true,
          },
        );

        this.softPromptsShown = decision.state.softPromptsShown;
        this.nextSoftPromptAt = decision.state.nextSoftPromptAt;
        this.previousCompletedCount = decision.state.previousCompletedCount;

        if (decision.clearActivePrompt) {
          this.showSoftPrompt.set(false);
          this.softPromptMessageId.set(null);
        }

        if (decision.showPrompt && lastCompleted !== undefined) {
          this.softPromptMessageId.set(lastCompleted.id);
          this.showSoftPrompt.set(true);
          this.feedbackPrompted.emit({ messageId: lastCompleted.id });
        }
      });
    });
    effect(() => {
      const deletingSessionIds = this.chatSessionDeletingIds();
      const chatSessions = this.chatSessions();
      const chatSessionsChanged = chatSessions !== previousChatSessions;
      previousChatSessions = chatSessions;
      if (deletingSessionIds.length > 0) {
        untracked(() => {
          this.optimisticChatSessionDeletingIds.set([]);
        });
        return;
      }
      if (
        chatSessionsChanged &&
        this.optimisticChatSessionDeletingIds().length > 0
      ) {
        untracked(() => {
          this.optimisticChatSessionDeletingIds.set([]);
        });
      }
    });

    // Only restores on error — status can't tell a real completion apart
    // from an aborted request settling back to 'ready'.
    let pendingOptionInFlight = false;
    effect(() => {
      const pending = this.pendingOptionSelection();
      const currentStatus = this.status();
      if (pending === null) {
        pendingOptionInFlight = false;
        return;
      }
      if (!pendingOptionInFlight) {
        if (currentStatus === 'submitted' || currentStatus === 'streaming') {
          pendingOptionInFlight = true;
        } else if (currentStatus === 'error') {
          untracked(() => {
            this.pendingOptionSelection.set(null);
          });
        }
        return;
      }
      if (currentStatus === 'submitted' || currentStatus === 'streaming') {
        return;
      }
      if (currentStatus === 'error') {
        untracked(() => {
          pendingOptionInFlight = false;
          this.pendingOptionSelection.set(null);
        });
      }
    });

    // Only this effect dismisses; correlated by selectionId so a stale
    // result (e.g. a retry reusing the same messageId) is dropped.
    effect(() => {
      const result = this.optionSendResult();
      if (result === null) {
        return;
      }
      const pending = this.pendingOptionSelection();
      if (pending === null || pending.selectionId !== result.selectionId) {
        return;
      }
      untracked(() => {
        if (result.success) {
          this.dismissedOptionMessageIds.update((ids) =>
            new Set(ids).add(pending.messageId),
          );
        }
        this.pendingOptionSelection.set(null);
      });
    });

    // Keeps the end of the conversation in view while a multi-step task runs.
    // The prompt-pinning effect below only fires once per turn, so without this
    // the checklist and the streaming reply drift below the fold.
    afterRenderEffect(() => {
      this.messages();
      this.plan();
      this.status();
      if (!this.isWorkingThroughChain()) {
        return;
      }
      const container = this.scrollContainer()?.nativeElement;
      if (!container) {
        return;
      }
      container.scrollTo({
        top: container.scrollHeight,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    });

    // Scrolls the newest prompt to the top on submit and on initial load. Tracks
    // messages so a turn missing from the DOM on first render still gets pinned.
    afterRenderEffect(() => {
      this.messages();
      const turnId = this.currentTurnId();
      const container = this.scrollContainer()?.nativeElement;
      if (
        turnId === null ||
        !container ||
        turnId === this.turnIdScrolledToTop
      ) {
        return;
      }
      const turn = container.querySelector<HTMLElement>(
        `[data-turn-id="${CSS.escape(turnId)}"]`,
      );
      if (!turn) {
        return;
      }
      this.turnIdScrolledToTop = turnId;
      if (this.scrollCurrentTurnFrame !== undefined) {
        cancelAnimationFrame(this.scrollCurrentTurnFrame);
        this.scrollCurrentTurnFrame = undefined;
      }
      const scrollToCurrentTurn = (behavior: ScrollBehavior): void => {
        const paddingTop = parseFloat(
          getComputedStyle(container).paddingTop || '0',
        );
        container.scrollTo({
          // offsetTop is measured from the container's padding edge; subtracting it
          // back leaves the prompt the same gap from the top as from the sides.
          top: turn.offsetTop - paddingTop,
          behavior,
        });
      };
      if (prefersReducedMotion()) {
        scrollToCurrentTurn('auto');
        return;
      }
      this.scrollCurrentTurnFrame = requestAnimationFrame(() => {
        scrollToCurrentTurn('smooth');
        this.scrollCurrentTurnFrame = undefined;
      });
    });

    afterRenderEffect(() => {
      if (this.openPanels().includes('chat')) {
        this.chatInput()?.nativeElement.focus();
      }
    });

    afterRenderEffect(() => {
      this.pendingAttachments();
      this.attachmentError();

      const el = this.attachmentListContainer()?.nativeElement;
      if (el) {
        el.scrollLeft = el.scrollWidth;
      }
    });
  }

  onAccordionChange(value: AccordionValue): void {
    if (value == null) {
      return;
    }
    const nextPanels = Array.isArray(value)
      ? value.map(String)
      : [String(value)];
    this.openPanels.set(nextPanels);
    if (!nextPanels.includes('chat')) {
      this.chatHistoryPanelOpen.set(false);
    }
  }

  onChatHistoryToggle(event: Event): void {
    event.stopPropagation();
    const isOpen = !this.chatHistoryPanelOpen();
    if (isOpen && this.chatHistoryBusy()) {
      return;
    }
    this.chatHistoryPanelOpen.set(isOpen);
    if (isOpen) {
      this.chatHistoryOpened.emit();
    }
  }

  onChatSessionsDeleteRequested(deliverableIds: UUID[]): void {
    this.optimisticChatSessionDeletingIds.set(deliverableIds);
    this.chatSessionsDeleteRequested.emit(deliverableIds);
  }

  onChatSessionsLoadMoreRequested(): void {
    this.chatSessionsLoadMoreRequested.emit();
  }

  onChatSessionsSearchRequested(searchTerm: string): void {
    this.chatSessionsSearchRequested.emit(searchTerm);
  }

  onChatSessionsSortChanged(sort: ChatSessionSort): void {
    this.chatSessionsSortChanged.emit(sort);
  }

  onChatSessionsScopeChanged(scope: ChatSessionScope): void {
    this.chatSessionsScopeChanged.emit(scope);
  }

  onThumbsUp(message: UIMessage) {
    this.feedbackState.update((state) => ({ ...state, [message.id]: 'up' }));
    this.feedbackComment.set('');
    this.messageIdShowingFeedback.set(null);
    this.messageIdThankYouShown.set(null);
    this.markFeedbackSubmitted(message.id);
    this.thumbsUp.emit({ message, prompted: 'voluntary' });
  }

  onThumbsDown(message: UIMessage) {
    this.feedbackState.update((state) => ({ ...state, [message.id]: 'down' }));
    this.messageIdThankYouShown.set(null);
    this.messageIdShowingFeedback.set(message.id);
    this.feedbackPrompted.emit({ messageId: message.id });
  }

  onFeedbackSubmitted(
    submission: FeedbackFormSubmission,
    message: UIMessage,
    prompted: 'voluntary' | 'soft' = 'voluntary',
  ): void {
    const payload: FeedbackEmission = {
      message,
      prompted,
      ...(submission.comment === '' ? {} : { comment: submission.comment }),
      ...(submission.categories.length > 0
        ? { categories: submission.categories }
        : {}),
      ...(submission.rating === undefined ? {} : { rating: submission.rating }),
    };
    if (submission.direction === 'up') {
      this.thumbsUp.emit(payload);
    } else {
      this.thumbsDown.emit(payload);
    }
    this.markFeedbackSubmitted(message.id);
    this.resetMessageFeedback();
    this.messageIdThankYouShown.set(message.id);
  }

  onFeedbackDismissed(): void {
    this.resetMessageFeedback();
  }

  onSoftPromptSubmitted(submission: FeedbackFormSubmission): void {
    const messageId = this.softPromptMessageId();
    if (messageId === null) return;
    const message = this.messages().find((m) => m.id === messageId);
    if (message !== undefined) {
      this.onFeedbackSubmitted(submission, message, 'soft');
    }
    this.showSoftPrompt.set(false);
    this.softPromptMessageId.set(null);
  }

  onSoftPromptSkipped(): void {
    const messageId = this.softPromptMessageId();
    if (messageId !== null) {
      this.feedbackSkipped.emit({ messageId });
    }
    this.showSoftPrompt.set(false);
    this.softPromptMessageId.set(null);
  }

  resetMessageFeedback(): void {
    this.messageIdShowingFeedback.set(null);
    this.messageIdThankYouShown.set(null);
    this.feedbackComment.set('');
  }

  private markFeedbackSubmitted(messageId: string): void {
    this.feedbackSubmitted.update((state) => ({ ...state, [messageId]: true }));
  }

  onSuggestionClick(suggestion: string): void {
    if (!this.tryLockSuggestionDispatch()) {
      return;
    }

    this.suggestionClicked.emit(suggestion);
  }

  onVisualSuggestionSelected(suggestion: InsightSuggestion): void {
    if (!this.tryLockSuggestionDispatch()) {
      return;
    }

    this.selectedVisualSuggestion.emit(suggestion);
  }

  private toInsightViewModel(i: InsightData['insights'][number]): {
    name: string;
    insight: InsightData['insights'][number];
    highchartsData: HighchartsData;
  } {
    const dataset = i.elements.objects.map((e) => {
      assertIsUUID(e.id);
      return e.id;
    });
    const content: HighchartsContent = {
      dataset,
      configuration: {
        visualizationId: i.visualizationId,
        chartModifier: {
          mappingSpec: i.mappingSpec as MappingSpec | undefined,
        },
      },
      needsPreview: false,
    };
    const highchartsData: HighchartsData = {
      sourceId: generateUUID(),
      content$: of(content),
    };
    return {
      name: i.name,
      insight: i,
      highchartsData,
    };
  }

  onInsightChartClick(insight: InsightData['insights'][number]): void {
    this.insightChartClicked.emit(insight);
  }

  onOptionClick(messageId: string, label: string): void {
    if (!this.tryLockOptionDispatch()) {
      return;
    }

    const selection: OptionSelection = {
      selectionId: generateUUID(),
      messageId,
      label,
    };
    this.pendingOptionSelection.set(selection);
    this.optionSelected.emit(selection);
  }

  onNavigationLinkClick(link: NavigationLink): void {
    this.navigationRequested.emit(link.data);
  }

  onConnectMcpClick(prompt: {
    mcpId: string;
    mcpName: string;
    endpointUrl: string;
  }): void {
    this.connectMcpClicked.emit(prompt);
  }

  onAttachClick(): void {
    this.fileInput()?.nativeElement.click();
  }

  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    // Copy the files out before resetting the input: `input.files` is a live
    // FileList, so clearing `input.value` (to allow re-picking the same file)
    // would empty it before we read it.
    const selectedFiles = input.files === null ? [] : Array.from(input.files);
    input.value = '';
    if (selectedFiles.length === 0) {
      return;
    }

    const acceptedFiles: File[] = [];
    const rejectedAttachments: RejectedAttachment[] = [];
    let rejection: Omit<AttachmentError, 'precedingAttachmentCount'> | null =
      null;
    for (const file of selectedFiles) {
      const reason = validateAttachment(file);
      if (reason === null) {
        acceptedFiles.push(file);
        continue;
      }
      rejectedAttachments.push({ ...fileAttachmentMetric(file), reason });
      if (reason === 'size') {
        rejection = {
          reason,
          filename: file.name,
          maxMb: Math.round(maxAttachmentSizeBytes(file) / (1024 * 1024)),
        };
      } else {
        rejection = { reason, filename: file.name };
      }
    }
    if (rejection !== null) {
      this.attachmentError.set({
        ...rejection,
        // This batch's accepted files are appended below, after this file
        // is read, so the error's position must already account for them.
        precedingAttachmentCount:
          this.pendingAttachments().length + acceptedFiles.length,
      });
    }
    if (rejectedAttachments.length > 0) {
      this.attachmentsRejected.emit(rejectedAttachments);
    }

    if (acceptedFiles.length === 0) {
      return;
    }
    this.isConvertingAttachments.set(true);
    try {
      const conversions = await Promise.allSettled(
        acceptedFiles.map(fileToFileUIPart),
      );
      const converted: FileUIPart[] = [];
      const conversionFailures: RejectedAttachment[] = [];
      let firstFailedFilename: string | null = null;
      conversions.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          converted.push(result.value);
          return;
        }
        const file = acceptedFiles[index];
        console.error('Failed to read attachment', result.reason);
        conversionFailures.push({
          ...fileAttachmentMetric(file),
          reason: 'conversion',
        });
        firstFailedFilename ??= file.name;
      });
      if (converted.length > 0) {
        this.pendingAttachments.update((current) => [...current, ...converted]);
      }
      if (conversionFailures.length > 0) {
        this.attachmentError.set({
          reason: 'conversion',
          filename: firstFailedFilename ?? '',
          precedingAttachmentCount: this.pendingAttachments().length,
        });
        this.attachmentsRejected.emit(conversionFailures);
      }
    } finally {
      this.isConvertingAttachments.set(false);
    }
  }

  removeAttachment(attachment: FileUIPart): void {
    const removedIndex = this.pendingAttachments().indexOf(attachment);
    this.pendingAttachments.update((current) =>
      current.filter((existing) => existing !== attachment),
    );
    // Keep the error at its correct relative position: removing an
    // attachment that occurred before it shifts everything after it back.
    const error = this.attachmentError();
    if (
      removedIndex !== -1 &&
      error !== null &&
      removedIndex < error.precedingAttachmentCount
    ) {
      this.attachmentError.set({
        ...error,
        precedingAttachmentCount: error.precedingAttachmentCount - 1,
      });
    }
  }

  private filePreview$(
    part: FileUIPart,
  ): Observable<string | null> | undefined {
    const deliverableId = parseDeliverableReferenceUrl(part.url);
    if (deliverableId === undefined) {
      return of(part.url);
    }
    const localPreview = this.localAttachmentPreviews()[deliverableId];
    return localPreview !== undefined ? of(localPreview) : undefined;
  }

  private fileParts(message: UIMessage): FileUIPart[] {
    return message.parts.filter(
      (part): part is FileUIPart => part.type === 'file',
    );
  }

  protected onDownloadAttachment(part: FileUIPart): void {
    this.downloadAttachment.emit(part);
  }

  sendMessage(message: string, $event: Event) {
    $event.preventDefault();
    const trimmed = message.trim();
    const files = this.pendingAttachments();
    if (
      this.isConvertingAttachments() ||
      this.isUploadingAttachments() ||
      (trimmed === '' && files.length === 0) ||
      this.status() !== 'ready' ||
      !!this.attachmentError()
    ) {
      return;
    }
    // While the soft prompt is open the user must rate the previous
    // response before sending another message — the card is the only
    // valid next action.
    if (this.showSoftPrompt()) {
      return;
    }

    this.resetMessageFeedback();
    this.inputVal.set('');
    this.pendingAttachments.set([]);
    this.attachmentError.set(null);
    this.resetInputHeight();
    this.messageSent.emit({ text: trimmed, files });
  }

  autoResizeInput(): void {
    const textarea = this.chatInput()?.nativeElement;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  private resetInputHeight(): void {
    const textarea = this.chatInput()?.nativeElement;
    if (!textarea) return;
    textarea.style.height = 'auto';
  }

  stopMessage(): void {
    this.stopRequested.emit();
  }

  onClearClick(event: Event): void {
    // The button sits inside the accordion header — stop the click
    // from toggling the panel open/closed.
    event.stopPropagation();
    this.resetMessageFeedback();
    this.clearRequested.emit();
  }

  ngOnDestroy(): void {
    if (this.unlockSuggestionDispatchTimer) {
      clearTimeout(this.unlockSuggestionDispatchTimer);
      this.unlockSuggestionDispatchTimer = undefined;
    }
    if (this.unlockOptionDispatchTimer) {
      clearTimeout(this.unlockOptionDispatchTimer);
      this.unlockOptionDispatchTimer = undefined;
    }
    if (this.scrollCurrentTurnFrame !== undefined) {
      cancelAnimationFrame(this.scrollCurrentTurnFrame);
      this.scrollCurrentTurnFrame = undefined;
    }
  }

  private tryLockSuggestionDispatch(): boolean {
    if (this.isProcessing() || this.suggestionDispatchState.isLocked()) {
      return false;
    }

    patchState(this.suggestionDispatchState, { isLocked: true });

    if (this.unlockSuggestionDispatchTimer) {
      clearTimeout(this.unlockSuggestionDispatchTimer);
    }

    this.unlockSuggestionDispatchTimer = setTimeout(() => {
      patchState(this.suggestionDispatchState, { isLocked: false });
      this.unlockSuggestionDispatchTimer = undefined;
    }, this.suggestionDispatchCooldownMs);

    return true;
  }

  private tryLockOptionDispatch(): boolean {
    if (this.isProcessing() || this.optionDispatchState.isLocked()) {
      return false;
    }

    patchState(this.optionDispatchState, { isLocked: true });

    if (this.unlockOptionDispatchTimer) {
      clearTimeout(this.unlockOptionDispatchTimer);
    }

    this.unlockOptionDispatchTimer = setTimeout(() => {
      patchState(this.optionDispatchState, { isLocked: false });
      this.unlockOptionDispatchTimer = undefined;
    }, this.optionDispatchCooldownMs);

    return true;
  }
}

function isRenderableDataPart(part: { type: string }): boolean {
  return part.type.startsWith('data-') && part.type !== 'data-thinking';
}

function prefersReducedMotion(): boolean {
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    'Cypress' in window
  );
}
